import React, { useContext, useEffect, useReducer, useState } from 'react';
import { localizationReducer } from './localizationReducer';
import {
	IDeleteWordingData,
	LocalizationContext,
	localizationInitialState,
	TLocalizationSelectedItem,
	TLocalizationSettingsMode,
	TTypeOfModalWindow,
} from './localizationContext';
import { LocalizationActionsTypes } from './localizationActions';
import { AppContext, IAppContext } from 'appState/appContext';
import {
	EditRestaurantContext,
	IEditRestaurantContext,
} from '../../../../../editRestaurantState/editRestaurantContext';
import { useFetchData } from 'hooks/useFetchData';
import {
	IGetRestaurantLocalizationInfo,
	IGetRestaurantLocalizationInfoArgs,
	IRestaurantLocalizationGroup,
	IUpdateRestaurantLocalization,
} from 'types';
import { getRestaurantLocalizationInfo } from 'backend/restaurants';
import { deleteWording } from 'backend/adminSettings';

const LocalizationState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(localizationReducer, localizationInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const { IDSelectedCustomer, visibleForm } = useContext<IEditRestaurantContext>(EditRestaurantContext);
	const [isFirstRender, setIsFirstRender] = useState<boolean>(true);
	const [needRequest, setNeedRequest] = useState<boolean>(false);

	const params = new URL(document.location.href).searchParams;
	const restaurantID = params.get('restaurantID');

	const needLocalizationDataRequest =
		IDSelectedCustomer && restaurantID && visibleForm === 'localizationForm' && isFirstRender;

	const { response, refetch, loading } = useFetchData<
		IGetRestaurantLocalizationInfo,
		IGetRestaurantLocalizationInfoArgs
	>(needRequest ? getRestaurantLocalizationInfo : null, {
		customerID: IDSelectedCustomer || '',
		restaurantID: restaurantID || '',
	});

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		setNeedRequest(!!needLocalizationDataRequest);
	}, [needLocalizationDataRequest]);

	useEffect(() => {
		setNeedRequest(!!state.needRefetchWordings);
	}, [state.needRefetchWordings]);

	useEffect(() => {
		if (state.needRefetchWordings) {
			refetch();
			dispatch({
				type: LocalizationActionsTypes.REFETCH_WORDINGS,
				payload: {
					needRefetch: false,
				},
			});
		}
	}, [state.needRefetchWordings]);

	useEffect(() => {
		refetch();
	}, [IDSelectedCustomer, restaurantID, visibleForm]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: LocalizationActionsTypes.SET_ALL_DATA,
				payload: {
					useCustomerSettings: response.data.useCustomerSettings,
					groups: response.data.groups,
				},
			});
			setIsFirstRender(false);
		}
	}, [response]);

	const refetchWordings = (value: boolean) => {
		dispatch({
			type: LocalizationActionsTypes.REFETCH_WORDINGS,
			payload: {
				needRefetch: value,
			},
		});
	};

	const setGroup = (value: TLocalizationSelectedItem | null) => {
		dispatch({
			type: LocalizationActionsTypes.SET_GROUP,
			payload: {
				group: value,
			},
		});
	};

	const setLanguage = (value: TLocalizationSelectedItem | null) => {
		dispatch({
			type: LocalizationActionsTypes.SET_LANGUAGE,
			payload: {
				language: value,
			},
		});
	};

	const removeWording = async (groupId: string, wordingId: string) => {
		try {
			showLoader();
			await deleteWording(wordingId, 'Restaurant');

			const updatedGroups: IRestaurantLocalizationGroup[] = state.groups.map((group) =>
				group.id === groupId ? { ...group, items: group.items.filter((item) => item.id !== wordingId) } : group,
			);

			dispatch({
				type: LocalizationActionsTypes.REMOVE_WORDING,
				payload: {
					updatedGroups,
				},
			});
			dispatch({
				type: LocalizationActionsTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'success',
				},
			});
		} catch (err) {
			dispatch({
				type: LocalizationActionsTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'error',
				},
			});
		} finally {
			hideLoader();
		}
	};

	// useEffect(() => {
	// 	const init = response?.data.useCustomerSettings;
	// 	if (init !== state.useCustomerSettings) {
	// 		restaurantID &&
	// 			updateRestaurantLocalization({
	// 				ownerId: restaurantID,
	// 				useCustomerSettings: state.useCustomerSettings,
	// 				groups: state.groups,
	// 			});
	// 		refetchWordings(true);
	// 	}
	// }, [response, state.useCustomerSettings]);

	const toggleUseCustomerSettings = () => {
		dispatch({
			type: LocalizationActionsTypes.TOGGLE_USE_CUSTOMER_SETTINGS,
		});
	};

	const addInvalidField = (value: string) => {
		dispatch({
			type: LocalizationActionsTypes.ADD_INVALID_FIELD,
			payload: {
				invalidField: value,
			},
		});
	};

	const clearInvalidFields = () => {
		dispatch({
			type: LocalizationActionsTypes.CLEAR_INVALID_FIELDS,
		});
	};

	const setDataForSubmit = (value: IUpdateRestaurantLocalization | null) => {
		dispatch({
			type: LocalizationActionsTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				data: value,
			},
		});
	};

	const setDeleteWordingData = (value: IDeleteWordingData | null) => {
		dispatch({
			type: LocalizationActionsTypes.SET_DELETE_WORDING_DATA,
			payload: {
				deleteWordingData: value,
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: LocalizationActionsTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const changeMode = (value: TLocalizationSettingsMode) => {
		dispatch({
			type: LocalizationActionsTypes.CHANGE_MODE,
			payload: {
				mode: value,
			},
		});
	};

	const updateGroups = (value: IRestaurantLocalizationGroup[]) => {
		dispatch({
			type: LocalizationActionsTypes.UPDATE_GROUPS,
			payload: {
				updatedGroups: value,
			},
		});
	};

	return (
		<LocalizationContext.Provider
			value={{
				refetchWordings,
				setGroup,
				setLanguage,
				removeWording,
				toggleUseCustomerSettings,
				addInvalidField,
				clearInvalidFields,
				setDataForSubmit,
				setDeleteWordingData,
				changeTypeOfModalWindow,
				changeMode,
				updateGroups,
				needRefetchWordings: state.needRefetchWordings,
				loading,
				selectedGroup: state.selectedGroup,
				selectedLanguage: state.selectedLanguage,
				useCustomerSettings: state.useCustomerSettings,
				groups: state.groups,
				invalidFields: state.invalidFields,
				dataForSubmit: state.dataForSubmit,
				deleteWordingData: state.deleteWordingData,
				typeOfModalWindow: state.typeOfModalWindow,
				mode: state.mode,
			}}>
			{children}
		</LocalizationContext.Provider>
	);
};

export default LocalizationState;
